import {
  createDarkTheme,
  createLightTheme,
  type BrandVariants,
  type Theme,
} from "@fluentui/react-components";
import type { ThemeMode } from "@contexts/ThemeContext";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/700.css";

export const brandPalette = {
  navy: "#35495E",
  blue: "#3D96D2",
  yellow: "#FDE34B",
  green: "#4EB96F",
  red: "#C91F41",
} as const;

// Navy-blue brand ramp used by Fluent brand tokens.
const otlBrand: BrandVariants = {
  10: "#0b111a",
  20: "#131e2b",
  30: "#1a2a3c",
  40: "#22364d",
  50: "#2b435f",
  60: "#35495E",
  70: "#4e6070",
  80: "#687785",
  90: "#8899A6",
  100: "#a2b0ba",
  110: "#bcc7ce",
  120: "#d5dde2",
  130: "#e2e8ec",
  140: "#ecf0f3",
  150: "#f4f6f8",
  160: "#f8f9fa",
};

const baseOverrides: Partial<Theme> = {
  fontFamilyBase: "'Montserrat', sans-serif",
  fontFamilyMonospace: "'Fira Code', monospace",
  fontSizeBase200: "12px",
  fontSizeBase300: "13px",
  fontSizeBase400: "14px",
  fontSizeBase500: "16px",
  borderRadiusSmall: "6px",
  borderRadiusMedium: "8px",
  borderRadiusLarge: "12px",
  borderRadiusXLarge: "16px",
  colorNeutralBackground1: "#ffffff",
  colorNeutralBackground2: "#f8f9fa",
  colorNeutralBackground3: "#eef0f2",
  colorNeutralBackground4: "#e4e8ec",
  colorNeutralStroke1: "#d5dae0",
  colorNeutralStroke2: "#c0c8d0",
  colorNeutralForeground1: "#1e2d3b",
  colorStatusSuccessBackground1: "#4eb96f",
  colorStatusSuccessForeground1: "#eaf6ee",
  colorStatusDangerBackground1: "#c91f41",
  colorStatusDangerForeground1: "#fceaed",
  colorStatusWarningBackground1: "#FDE34B",
  colorStatusWarningForeground1: "#fef9e6",
  colorBrandForeground1: "#35495E",
  colorBrandForeground2: "#2b435f",
  colorBrandBackground: "#35495E",
  colorBrandBackgroundHover: "#2b435f",
  colorBrandBackgroundPressed: "#22364d",
  colorBrandForegroundOnLight: "#35495E",
  colorBrandForegroundLink: "#35495E",
  colorBrandForegroundLinkHover: "#2b435f",
  colorBrandForegroundLinkPressed: "#22364d",
};

/**
 * Our own variables, carried in the theme object. Fluent writes every key in a
 * theme out as a CSS variable on the class it generates, and puts that class on
 * portal mount nodes too — so dialogs, menus and tooltips get these as well,
 * which a variable set on the provider element would not reach.
 *
 * otlSourceIconPlate is what source logos sit on. They are brand artwork drawn
 * for a white page, and the near-black ones — GitHub's mark, the Dataverse cube
 * — are a blank square on a dark surface. Under the light themes it is nothing
 * at all, since what is behind them is already white. The shape of the plate —
 * its padding and radius — is the same in every theme and lives with the rule,
 * in style.scss; only whether anything is painted changes here.
 */
export interface OtlTheme extends Theme {
  otlSourceIconPlate: string;
  /**
   * Categorical chart series slots, assigned in order and never cycled.
   *
   * Not drawn from the Fluent palette tokens. Those gave two near-identical
   * dark navies for series 1 and 2 — indistinguishable where two lines overlap
   * — and the brand token keeps its navy in dark mode, where it all but
   * disappears into the surface. These are three separated hues, checked for
   * colour-blind separation and contrast against each surface, and stepped
   * independently for dark rather than flipped from light.
   */
  otlChartSeries1: string;
  otlChartSeries2: string;
  otlChartSeries3: string;
}

export const otlLightTheme: OtlTheme = {
  ...createLightTheme(otlBrand),
  otlSourceIconPlate: "transparent",
  otlChartSeries1: "#2a78d6",
  otlChartSeries2: "#eb6834",
  otlChartSeries3: "#1baf7a",
  ...baseOverrides,
  // Secondary foreground must be set per theme, not in baseOverrides: the value
  // that reads as "muted" on a dark surface is unreadable on a white one. This
  // is 5.5:1 on colorNeutralBackground1, where #aab5be was only ~2.1:1.
  colorNeutralForeground3: "#5c6b7a",
};

export const otlDarkTheme: OtlTheme = {
  ...createDarkTheme(otlBrand),
  otlSourceIconPlate: "#ffffff",
  otlChartSeries1: "#3987e5",
  otlChartSeries2: "#d95926",
  otlChartSeries3: "#199e70",
  ...baseOverrides,
  colorNeutralBackground1: "#111820",
  colorNeutralBackground2: "#1a2330",
  colorNeutralBackground3: "#223040",
  colorNeutralForeground1: "#e8edf2",
  colorNeutralForeground3: "#aab5be",
  colorNeutralStroke1: "#2d3f52",
};

// "classic" deliberately shares the light token set: it differs from light only
// in chrome (navy header and left nav), which is driven by CSS modifier classes
// rather than Fluent tokens. Content surfaces are identical in both.
export const getOtlTheme = (themeMode: ThemeMode): OtlTheme =>
  themeMode === "dark" ? otlDarkTheme : otlLightTheme;
